import React, { useEffect, useRef } from 'react';
import { FaCheck, FaCheckDouble, FaExclamationCircle, FaClock } from 'react-icons/fa';
import { formatBubbleTime, formatFullTimestamp, formatDaySeparator, isNewDay } from './conversationFormat';

// Transcript for one thread. Inbound sits left in grey, outbound right in brand cyan, which is the
// convention every messaging app uses, so no legend is needed.

// Delivery state shown under an outbound bubble. Inbound messages carry no state.
const DELIVERY_META = {
  sent: { Icon: FaCheck, label: 'Sent', cls: 'text-white/70' },
  delivered: { Icon: FaCheckDouble, label: 'Delivered', cls: 'text-white/90' },
  failed: { Icon: FaExclamationCircle, label: 'Failed', cls: 'text-red-100' },
  pending: { Icon: FaClock, label: 'Sending', cls: 'text-white/60' },
};

const DaySeparator = ({ iso }) => (
  <li className="flex items-center gap-3 py-3" aria-hidden="true">
    <span className="flex-1 h-px bg-gray-200" />
    <span className="text-xs font-medium text-gray-500">{formatDaySeparator(iso)}</span>
    <span className="flex-1 h-px bg-gray-200" />
  </li>
);

const MessageBubble = ({ message }) => {
  const isOutbound = message.direction === 'outbound';
  const delivery = DELIVERY_META[message.status] || DELIVERY_META.pending;
  const failed = message.status === 'failed';

  return (
    <li className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[min(32rem,80%)] ${isOutbound ? 'items-end' : 'items-start'} flex flex-col`}>
        <div
          className={`rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap break-words ${
            isOutbound
              ? failed
                ? 'bg-red-600 text-white rounded-br-sm'
                : 'bg-jims-cyan text-white rounded-br-sm'
              : 'bg-gray-100 text-gray-900 rounded-bl-sm'
          }`}
        >
          {/* Screen readers get the direction and full timestamp the visual layout conveys. */}
          <span className="sr-only">{isOutbound ? 'Sent' : 'Received'} {formatFullTimestamp(message.createdAt)}: </span>
          {message.text}

          {isOutbound && (
            <span className={`mt-1 flex items-center justify-end gap-1 text-[11px] ${delivery.cls}`}>
              {formatBubbleTime(message.createdAt)}
              <delivery.Icon className="h-3 w-3" aria-hidden="true" />
              <span className="sr-only">{delivery.label}</span>
              {message.mock
                ? <span className="ml-1 uppercase font-semibold tracking-wide">mock</span>
                : message.testMode && <span className="ml-1 uppercase font-semibold tracking-wide">test</span>}
            </span>
          )}
        </div>

        {!isOutbound && (
          <span className="mt-1 px-1 text-[11px] text-gray-400">{formatBubbleTime(message.createdAt)}</span>
        )}
        {failed && message.errorMessage && (
          <span className="mt-1 px-1 text-[11px] text-red-600">{message.errorMessage}</span>
        )}
        {isOutbound && message.sentBy && (
          <span className="px-1 text-[11px] text-gray-400">by {message.sentBy}</span>
        )}
      </div>
    </li>
  );
};

const MessageThread = ({ messages, loading, truncated, emptyHint }) => {
  const endRef = useRef(null);
  const containerRef = useRef(null);
  const lastMessageId = messages.length ? messages[messages.length - 1].id : null;

  // Follow the conversation only when the operator is already at the bottom, so polling never yanks
  // them away from older messages they are reading.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 160) endRef.current?.scrollIntoView({ block: 'end' });
  }, [lastMessageId]);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-2 bg-white">
      {loading && messages.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">Loading messages…</p>
      ) : messages.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">{emptyHint || 'No messages in this conversation yet.'}</p>
      ) : (
        <>
          {truncated && (
            <p className="py-2 text-center text-xs text-gray-400">Showing the most recent messages only.</p>
          )}
          {/* Polite so a reply arriving mid-poll is announced without interrupting. */}
          <ul className="space-y-2" aria-live="polite" aria-relevant="additions">
            {messages.map((message, index) => (
              <React.Fragment key={message.id}>
                {isNewDay(message.createdAt, messages[index - 1]?.createdAt) && (
                  <DaySeparator iso={message.createdAt} />
                )}
                <MessageBubble message={message} />
              </React.Fragment>
            ))}
          </ul>
        </>
      )}
      <div ref={endRef} />
    </div>
  );
};

export default MessageThread;
